import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  alpha,
  Chip,
} from '@mui/material';
import {
  TrendingDown,
  Analytics,
  ErrorOutline,
  InfoOutlined,
  Warning,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Signal configuration with severity, icons, and descriptions
const getSignalConfig = (signal, type, t) => {
  const signalLower = signal.toLowerCase();
  
  // Technical signals
  if (type === 'technical') {
    if (signalLower.includes('early warning')) {
      const horizon = signal.match(/\((\d+)D\)/)?.[1] || '';
      return {
        severity: 'warning',
        icon: <Warning />,
        color: '#ea580c',
        gradient: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
        description: t('downtrend.signals.earlyWarningDesc', { defaultValue: 'Early warning for {{horizon}}-day timeframe. Risk is rising but downtrend not yet confirmed.', horizon }),
        category: t('downtrend.signals.earlyWarning', 'Early Warning'),
      };
    }
    if (signalLower.includes('high technical risk')) {
      return {
        severity: 'high',
        icon: <ErrorOutline />,
        color: '#e11d48',
        gradient: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)',
        description: t('downtrend.signals.highTechRiskDesc', 'High technical risk. Technical indicators show strong selling pressure.'),
        category: t('downtrend.signals.highRisk', 'High Risk'),
      };
    }
    if (signalLower.includes('downtrend confirmed')) {
      return {
        severity: 'critical',
        icon: <TrendingDown />,
        color: '#be123c',
        gradient: 'linear-gradient(135deg, #be123c 0%, #dc2626 100%)',
        description: t('downtrend.signals.downtrendConfirmedDesc', 'Downtrend confirmed. Price has broken key support levels.'),
        category: t('downtrend.signals.downtrendConfirmed', 'Downtrend Confirmed'),
      };
    }
  }
  
  // Macro signals
  if (type === 'macro') {
    if (signalLower.includes('high macro risk')) {
      return {
        severity: 'critical',
        icon: <Analytics />,
        color: '#e11d48',
        gradient: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)',
        description: t('downtrend.signals.highMacroRiskDesc', 'High macro risk. Macroeconomic environment is unfavorable for the market.'),
        category: t('downtrend.signals.highMacroRisk', 'High Macro Risk'),
      };
    }
    if (signalLower.includes('elevated macro risk')) {
      return {
        severity: 'warning',
        icon: <Warning />,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
        description: t('downtrend.signals.elevatedMacroRiskDesc', 'Elevated macro risk. Macro indicators need close monitoring.'),
        category: t('downtrend.signals.elevatedMacroRisk', 'Elevated Macro Risk'),
      };
    }
  }
  
  // Default
  return {
    severity: 'info',
    icon: <InfoOutlined />,
    color: '#64748b',
    gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
    description: signal,
    category: type === 'technical' ? t('downtrend.signals.technicalSignal', 'Technical Signal') : t('downtrend.signals.macroSignal', 'Macro Signal'),
  };
};

const SignalsSection = ({
  detectionData,
  expandedSections,
  toggleSection
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const techSignals = useMemo(() => {
    return (detectionData.technical_signals || []).map((signal, idx) => ({
      id: `tech-${idx}`,
      text: signal.text[i18n.language] || signal.text.vi || signal.text.en,
      config: getSignalConfig(signal.key, 'technical', t),
    }));
  }, [detectionData.technical_signals, i18n.language, t]);

  const macroSignals = useMemo(() => {
    return (detectionData.macro_signals || []).map((signal, idx) => ({
      id: `macro-${idx}`,
      text: signal.text[i18n.language] || signal.text.vi || signal.text.en,
      config: getSignalConfig(signal.key, 'macro', t),
    }));
  }, [detectionData.macro_signals, i18n.language, t]);

  const allSignals = [...techSignals, ...macroSignals];
  const totalSignals = allSignals.length;

  if (totalSignals === 0) {
    return null;
  }

  // Determine layout based on signal count
  const useCompactLayout = totalSignals <= 2;
  const useHorizontalLayout = totalSignals === 1;

  return (
    <Box sx={{ mb: 3 }}>
        {/* Signals Grid - Optimized */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: useHorizontalLayout
              ? '1fr'
              : useCompactLayout
              ? {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                }
              : {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
            gap: 2,
          }}
        >
            {/* Technical Signals */}
            {techSignals.map((signal) => {
              const { config } = signal;
              return (
                <Card
                  key={signal.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(config.color, 0.2)}`,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(config.color, 0.08)} 0%, ${alpha(config.color, 0.03)} 100%)`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 16px ${alpha(config.color, 0.2)}`,
                      borderColor: alpha(config.color, 0.4),
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 4,
                      height: '100%',
                      background: config.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, pl: 3.5 }}>
                    <Stack spacing={1.5}>
                      {/* Header - Compact */}
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: config.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 2px 6px ${alpha(config.color, 0.3)}`,
                            flexShrink: 0,
                          }}
                        >
                          {React.cloneElement(config.icon, {
                            sx: { color: 'white', fontSize: 18 },
                          })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Chip
                              label={config.category}
                              size="small"
                              sx={{
                                bgcolor: alpha(config.color, 0.15),
                                color: config.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                            <Chip
                              icon={<TrendingDown sx={{ fontSize: 12 }} />}
                              label={t('downtrend.signals.technical', 'Technical')}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                color: theme.palette.warning.main,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Signal Text - Compact */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            color: config.color,
                            mb: 0.5,
                            fontSize: '0.95rem',
                          }}
                        >
                          {signal.text}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {config.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}

            {/* Macro Signals */}
            {macroSignals.map((signal) => {
              const { config } = signal;
              return (
                <Card
                  key={signal.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(config.color, 0.2)}`,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(config.color, 0.08)} 0%, ${alpha(config.color, 0.03)} 100%)`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 16px ${alpha(config.color, 0.2)}`,
                      borderColor: alpha(config.color, 0.4),
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 4,
                      height: '100%',
                      background: config.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, pl: 3.5 }}>
                    <Stack spacing={1.5}>
                      {/* Header - Compact */}
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: config.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 2px 6px ${alpha(config.color, 0.3)}`,
                            flexShrink: 0,
                          }}
                        >
                          {React.cloneElement(config.icon, {
                            sx: { color: 'white', fontSize: 18 },
                          })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Chip
                              label={config.category}
                              size="small"
                              sx={{
                                bgcolor: alpha(config.color, 0.15),
                                color: config.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                            <Chip
                              icon={<Analytics sx={{ fontSize: 12 }} />}
                              label={t('downtrend.signals.macro', 'Macro')}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.main,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Signal Text - Compact */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            color: config.color,
                            mb: 0.5,
                            fontSize: '0.95rem',
                          }}
                        >
                          {signal.text}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {config.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
      </Box>
  );
};

export default SignalsSection;
